import React from "react";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import styled from "styled-components";
import {PAGE_URLS} from "constants/AppConstants.js";
import {ws} from "styles/tokens";

const HeaderBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 87px;
  padding: 16px 45px 16px 34px;
  background: ${ws.bg};
  border-bottom: 1px solid ${ws.line};

  @media (max-width: 560px) {
    padding: 16px 20px;
  }
`;

const Logo = styled.img`
  height: 49px;
  min-width: 0;
  cursor: pointer;

  @media (max-width: 560px) {
    height: 28px;
  }
`;

const CtaGroup = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  @media (max-width: 560px) {
    gap: 6px;
  }
`;

const LoginCta = styled.button<{filled?: boolean}>`
  padding: 10px 20px;
  border: 1px solid ${(props) => (props.filled ? "transparent" : ws.lineStrong)};
  border-radius: ${ws.radiusPill};
  background: ${(props) => (props.filled ? ws.accent : "transparent")};
  font-family: ${ws.fontBody};
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.filled ? ws.onAccent : ws.fg)};
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.2s,
    border-color 0.2s;

  @media (max-width: 560px) {
    padding: 8px 12px;
    font-size: 12px;
  }

  &:hover {
    background: ${(props) => (props.filled ? ws.accentDeep : "transparent")};
    border-color: ${(props) => (props.filled ? "transparent" : ws.fg)};
  }
`;

const CreateCta = styled(LoginCta)`
  background: ${ws.accent};
  border-color: transparent;
  color: ${ws.onAccent};

  &:hover {
    background: ${ws.accentDeep};
    border-color: transparent;
  }

  /* The landing hero repeats this call to action right below. */
  @media (max-width: 560px) {
    display: none;
  }
`;

interface Props {
  /** Shown on the landing so ads can split signup vs login. */
  showCreateAccount?: boolean;
  onCreateAccount?: () => void;
  /** Logs in on the current page instead of the sign-in page, e.g. to keep a payment link. */
  onLogin?: () => void;
}

function OnboardingHeader({showCreateAccount = false, onCreateAccount, onLogin}: Props) {
  const {t} = useTranslation();
  const history = useHistory();

  return (
    <HeaderBar>
      <Logo
        src="/assets/images/waterspeed-logo.svg"
        alt="Waterspeed"
        onClick={() => history.push(PAGE_URLS.signInPageUrl)}
      />
      <CtaGroup>
        <LoginCta
          type="button"
          filled={!showCreateAccount}
          onClick={onLogin ?? (() => history.push(PAGE_URLS.signInPageUrl))}
        >
          {t("onboarding.logInWebapp")}
        </LoginCta>
        {showCreateAccount && (
          <CreateCta type="button" onClick={onCreateAccount}>
            {t("onboarding.landing.createFreeAccount")}
          </CreateCta>
        )}
      </CtaGroup>
    </HeaderBar>
  );
}

export default OnboardingHeader;
